// Unified Promocode Configuration
// This file manages platform-agnostic promocode settings

export const PROMOCODE_CONFIG = {
  // Use existing table name for backward compatibility
  tableName: 'appsumo_promocodes',
  
  // Platform configurations (dynamic - can be managed via admin interface)
  platforms: {},

  // Plan type configurations (platform-agnostic)
  planTypes: {
    'lifetime': {
      billingCycle: 'yearly',
      description: 'Lifetime access with yearly billing',
      resetExtensionYears: 10
    },
    'monthly': {
      billingCycle: 'monthly',
      description: 'Monthly subscription',
      resetExtensionMonths: 1
    },
    'yearly': {
      billingCycle: 'yearly',
      description: 'Yearly subscription',
      resetExtensionYears: 1
    }
  },

  // Default settings
  defaults: {
    planType: 'lifetime',
    credits: 10000,
    expiryYears: 10,
    maxUses: 1
  },

  // Promocode format patterns
  patterns: {
    structured: /^([A-Z]{2})([LMY])(\d+[KM]?)(\d{4})([A-Z0-9]{8})$/,
    legacy: /^([A-Z0-9]{8,16})$/
  }
};

// Helper functions
export const PromocodeHelpers = {
  // Get platform by code
  getPlatformByCode(code) {
    this.ensurePlatformsLoaded();
    return Object.values(PROMOCODE_CONFIG.platforms).find(p => p.code === code) || null;
  },

  // Get platform by name
  getPlatformByName(name) {
    this.ensurePlatformsLoaded();
    return PROMOCODE_CONFIG.platforms[name.toLowerCase()] || null;
  },

  // Add platform dynamically
  // Platform management with localStorage persistence
  addPlatform(platformId, platformConfig) {
    PROMOCODE_CONFIG.platforms[platformId] = platformConfig;
    // Persist to localStorage (browser only)
    if (typeof window !== 'undefined' && window.localStorage && typeof window.localStorage.setItem === 'function') {
      window.localStorage.setItem('promocode_platforms', JSON.stringify(PROMOCODE_CONFIG.platforms));
    }
  },

  // Remove platform
  removePlatform(platformId) {
    delete PROMOCODE_CONFIG.platforms[platformId];
    // Persist to localStorage (browser only)
    if (typeof window !== 'undefined' && window.localStorage && typeof window.localStorage.setItem === 'function') {
      window.localStorage.setItem('promocode_platforms', JSON.stringify(PROMOCODE_CONFIG.platforms));
    }
  },

  // Load platforms from localStorage on initialization
  loadPlatformsFromStorage() {
    try {
      // Check if we're in a browser environment with proper localStorage
      if (typeof window !== 'undefined' && window.localStorage && typeof window.localStorage.getItem === 'function') {
        const stored = window.localStorage.getItem('promocode_platforms');
        if (stored) {
          PROMOCODE_CONFIG.platforms = JSON.parse(stored);
          console.log('Platforms loaded from localStorage:', PROMOCODE_CONFIG.platforms);
        }
      }
    } catch (error) {
      console.error('Failed to load platforms from localStorage:', error);
    }
  },

  // Ensure platforms are loaded before use
  ensurePlatformsLoaded() {
    if (typeof window !== 'undefined' && window.localStorage && typeof window.localStorage.getItem === 'function') {
      this.loadPlatformsFromStorage();
    }
  },

  // Get plan type configuration
  getPlanTypeConfig(planType) {
    return PROMOCODE_CONFIG.planTypes[planType] || PROMOCODE_CONFIG.planTypes.lifetime;
  },

  // Get credit system plan name (platform-agnostic)
  getCreditSystemPlan(platform, planType) {
    // Use platform name as subscription plan, capitalize first letter
    if (!platform) return 'Unknown'; // Fallback for unknown platforms
    return platform.charAt(0).toUpperCase() + platform.slice(1).toLowerCase();
  },

  // Get active platforms
  getActivePlatforms() {
    this.ensurePlatformsLoaded();
    return Object.entries(PROMOCODE_CONFIG.platforms)
      .filter(([_, platform]) => platform.isActive)
      .map(([key, platform]) => ({ key, ...platform }));
  }
};

// Note: Platforms are loaded lazily when needed to avoid localStorage issues during module import

export default PROMOCODE_CONFIG;
